import { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { currency, fmtDate } from "../../lib/status";
import { Spinner } from "../../components/Brand";
import { toast } from "sonner";

export default function Membership() {
    const { user, refreshMe } = useAuth();
    const [plans, setPlans] = useState(null);
    const [current, setCurrent] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        const [p, m] = await Promise.all([
            api.get("/membership/plans"),
            api.get("/membership/me"),
        ]);
        setPlans(p.data);
        setCurrent(m.data);
    };

    useEffect(() => {
        load();
    }, []);

    if (!plans) return <Spinner />;

    const subscribe = async (planId) => {
        setBusy(planId);
        try {
            await api.post("/membership/subscribe", { plan_id: planId });
            toast.success("Welcome to Luxurio Membership!");
            await refreshMe();
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not subscribe");
        } finally {
            setBusy(null);
        }
    };

    const cancel = async () => {
        if (!window.confirm("Cancel your membership? You'll keep benefits until expiry.")) return;
        try {
            await api.post("/membership/cancel");
            toast.success("Membership cancelled");
            await load();
        } catch (e) {
            toast.error("Failed");
        }
    };

    return (
        <div className="space-y-10">
            <div className="lx-label text-[#B2912A]">Membership</div>
            <div className="grid lg:grid-cols-12 gap-6 items-start">
                <div className="lg:col-span-7">
                    <h1 className="font-serif text-4xl sm:text-5xl text-[#180A21] leading-tight">
                        Become a <span className="italic lx-gold-text">Luxurio</span> member.
                    </h1>
                    <p className="text-[#555] mt-4 max-w-xl leading-relaxed">
                        Concierge-level access to every service in our catalogue — with
                        discounts, free monthly visits, and priority booking. Pay monthly,
                        cancel anytime.
                    </p>
                </div>

                {current && current.plan && (
                    <div
                        className="lg:col-span-5 relative overflow-hidden p-7 text-white"
                        style={{
                            background:
                                "linear-gradient(135deg, #180A21 0%, #2B1438 70%, #180A21 100%)",
                        }}
                        data-testid="current-membership-card"
                    >
                        <div className="absolute -right-10 -bottom-10 w-60 h-60 rounded-full bg-[#D4AF37]/10 blur-3xl" />
                        <div className="relative z-10">
                            <div className="lx-label text-[#D4AF37] mb-2">
                                Current membership
                            </div>
                            <div className="font-serif text-3xl text-[#D4AF37]">
                                {current.plan.name}
                            </div>
                            <div className="text-sm text-white/70 mt-1">
                                Renews on {fmtDate(current.expires_at, { hour: undefined, minute: undefined })}
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-white/10">
                                <div>
                                    <div className="lx-label text-white/60">You've saved</div>
                                    <div className="font-serif text-2xl">
                                        {currency(current.total_saved || 0, "AED")}
                                    </div>
                                </div>
                                <div>
                                    <div className="lx-label text-white/60">Services used</div>
                                    <div className="font-serif text-2xl">
                                        {current.services_used_this_period || 0}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={cancel}
                                data-testid="cancel-membership-btn"
                                className="mt-5 text-xs text-white/60 hover:text-white"
                            >
                                Cancel membership →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="grid md:grid-cols-3 gap-6">
                {plans.map((p) => {
                    const isCurrent = current?.plan_id === p.id;
                    const isPopular = p.tier === "premium";
                    return (
                        <div
                            key={p.id}
                            data-testid={`plan-${p.code}`}
                            className={`relative p-8 transition-all duration-500 ${
                                isPopular
                                    ? "bg-[#180A21] text-white"
                                    : "bg-white text-[#180A21] border border-[#E5E2DC]"
                            } ${isCurrent ? "ring-2 ring-[#D4AF37]" : ""}`}
                        >
                            {isPopular && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#180A21] text-[10px] tracking-[0.2em] uppercase font-semibold px-3 py-1">
                                    Most Popular
                                </div>
                            )}
                            <div
                                className={`lx-label mb-3 ${isPopular ? "text-[#D4AF37]" : "text-[#B2912A]"}`}
                            >
                                {p.tier}
                            </div>
                            <div
                                className={`font-serif text-3xl mb-1 ${isPopular ? "text-white" : "text-[#180A21]"}`}
                            >
                                {p.name}
                            </div>
                            <div
                                className={`text-sm mb-5 ${isPopular ? "text-white/60" : "text-[#555]"}`}
                            >
                                {p.tagline}
                            </div>
                            <div className="flex items-baseline gap-1 mb-6">
                                <span
                                    className={`font-serif text-5xl ${isPopular ? "text-[#D4AF37]" : "text-[#180A21]"}`}
                                >
                                    {currency(p.monthly_price, p.currency)}
                                </span>
                                <span
                                    className={`text-sm ${isPopular ? "text-white/60" : "text-[#555]"}`}
                                >
                                    /mo
                                </span>
                            </div>
                            <ul className="space-y-2.5 mb-7">
                                {p.perks.map((perk, i) => (
                                    <li
                                        key={i}
                                        className={`text-sm flex gap-3 items-start ${isPopular ? "text-white/90" : "text-[#180A21]"}`}
                                    >
                                        <span className="text-[#D4AF37] mt-0.5">✓</span>
                                        {perk}
                                    </li>
                                ))}
                            </ul>
                            {isCurrent ? (
                                <div className="text-center py-3 border border-[#D4AF37] text-[#D4AF37] uppercase tracking-wider text-xs font-semibold">
                                    Current plan
                                </div>
                            ) : (
                                <button
                                    onClick={() => subscribe(p.id)}
                                    disabled={busy === p.id}
                                    data-testid={`subscribe-${p.code}`}
                                    className={`w-full py-3 font-medium uppercase tracking-wider text-xs disabled:opacity-50 ${
                                        isPopular
                                            ? "bg-[#D4AF37] text-[#180A21] hover:bg-[#E8D399]"
                                            : "bg-[#180A21] text-white hover:bg-[#2B1438]"
                                    } transition`}
                                >
                                    {busy === p.id ? "…" : `Subscribe →`}
                                </button>
                            )}
                            <div
                                className={`text-[10px] mt-3 text-center ${isPopular ? "text-white/40" : "text-[#555]"}`}
                            >
                                Pays from your wallet (balance:{" "}
                                {currency(user?.wallet_balance || 0, "AED")})
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="lx-card p-7 text-center">
                <div className="lx-label text-[#B2912A] mb-2">How it works</div>
                <p className="text-sm text-[#555] max-w-2xl mx-auto leading-relaxed">
                    Subscribe with your Luxurio wallet. The discount applies
                    automatically to every booking you make — no codes, no fine print.
                    Cancel anytime; benefits last until expiry. New members are eligible
                    for our 30-day satisfaction guarantee.
                </p>
            </div>
        </div>
    );
}
